import React, { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { SiteLang } from './TranslateRoutes';

const allowed = ['en', 'fr', 'es'] as const;

// Langue utilisée pour construire les routes (cf. TranslateRoutes) au chargement de l'application.
// Les chemins traduits (evenements / events / eventio...) sont figés à ce moment-là.
const storedLang = (localStorage.getItem('lang') ?? '').toLowerCase();
const hadStoredLang = allowed.includes(storedLang as any);
export const routesLang = (hadStoredLang ? storedLang : 'en') as SiteLang;

const storageWorks = (): boolean => {
    try {
        localStorage.setItem('lang-probe', '1');
        localStorage.removeItem('lang-probe');
        return true;
    } catch {
        return false;
    }
};

const LangGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { lang } = useParams();
    const location = useLocation();

    const urlLang = lang && allowed.includes(lang as any) ? (lang as SiteLang) : undefined;
    const langMismatch = !!urlLang && urlLang !== routesLang;

    // La langue de l'URL fait foi (lien partage, collé dans un autre navigateur...) : si elle differe
    // de celle des routes chargees, on la memorise et on recharge pour reconstruire les routes
    // traduites. Sans stockage utilisable, on ne recharge pas (boucle infinie sinon).
    const adoptUrlLang = langMismatch && storageWorks();

    useEffect(() => {
        if (adoptUrlLang && urlLang) {
            localStorage.setItem('lang', urlLang);
            window.location.reload();
        }
    }, [adoptUrlLang, urlLang]);

    if (adoptUrlLang) return null;

    const preferred =
        (localStorage.getItem('lang') ??
            navigator.language?.split('-')[0] ??
            'en').toLowerCase();

    const targetLang = (allowed.includes(preferred as any) ? preferred : 'en') as string;

    // Si pas de langue OU langue invalide, on préfixe/switch la langue en préservant le reste du chemin
    if (!lang || !allowed.includes(lang as any)) {
        const segments = location.pathname.split('/').filter(Boolean);
        const tail = allowed.includes(segments[0] as any) ? segments.slice(1) : segments;
        const to = '/' + [targetLang, ...tail].join('/');
        return <Navigate to={to} replace />;
    }

    return <>{children}</>;
};

export default LangGuard;
